import { Box, Heading, Flex, useToast, Icon } from '@chakra-ui/react';
import { RiAddLine } from 'react-icons/ri';
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import debounce from 'lodash.debounce';
import { DefaultLayout } from '../../../_layout/DefaultLayout';
import { LinkButton } from '../../../../../components/LinkButton';
import { maskShortDate } from '../../../../../utils/formatters/handleMask';
import {
  IAdvertisement,
  AdvertisementTable,
} from './components/AdvertisementTable';
import { listAdvertisementsService } from '../../../../../services/Advertisements/ListAdvertisementsService';
import { useAuth } from '../../../../../hooks/auth';
import { translateError } from '../../../../../utils/errors';

interface ILoadAdvertisementsProps {
  categoryId?: string;
  isActive?: boolean;
  search?: string;
  sellerId?: string;
  status?: 'APPROVED' | 'REJECTED' | 'REVIEW_PENDING';
  limit?: number;
  order?: 'ASC' | 'DESC';
  page?: number;
  sort?: 'title' | 'isActive' | 'expirationDate' | 'createdAt';
}

export const AdvertisementList = (): JSX.Element => {
  const toast = useToast();
  const { user } = useAuth();

  const [advertisementsList, setAdvertisementsList] = useState<
    IAdvertisement[]
  >([]);
  const [currentFilters, setCurrentFilters] =
    useState<{ [key: string]: string }>();
  const [currentOrdering, setCurrentOrdering] = useState<'ASC' | 'DESC'>('ASC');
  const [currentPage, setCurrentPage] = useState(1);
  const [filterBy, setFilterBy] = useState('title');
  const [sortBy, setSortBy] = useState<
    'title' | 'isActive' | 'expirationDate' | 'createdAt'
  >('createdAt');
  const [totalPages, setTotalPages] = useState<number>();

  const loadAdvertisements = useCallback(
    async ({
      categoryId,
      isActive,
      search,
      sellerId,
      status,
      limit = 20,
      order,
      page,
      sort,
    }: ILoadAdvertisementsProps) => {
      try {
        const advertisements = await listAdvertisementsService({
          categoryId,
          isActive,
          search: search || undefined,
          sellerId: user.role !== 'ADMIN' ? user.sellerId : sellerId,
          status,
          limit,
          order,
          page,
          sort,
        });

        const parsedAdvertisements = advertisements.items.map(
          (advertisement) => ({
            ...advertisement,
            formattedCreatedAt: maskShortDate(advertisement.createdAt),
            formattedExpirationDate: advertisement.expirationDate
              ? maskShortDate(advertisement.expirationDate)
              : 'não expira',
          }),
        );

        setTotalPages(advertisements.pages);
        setAdvertisementsList(parsedAdvertisements);
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status !== 401) {
          toast({
            title: 'Falha ao carregar dados',
            description:
              translateError({ message: err.response?.data.message }) ||
              'Ocorreu um erro ao carregar os dados dos anúncios, tente novamente',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      }
    },
    [toast, user.role, user.sellerId],
  );

  useEffect(() => {
    loadAdvertisements({
      ...currentFilters,
      order: currentOrdering,
      page: currentPage,
      sort: sortBy,
    });
  }, [
    currentFilters,
    currentPage,
    sortBy,
    loadAdvertisements,
    currentOrdering,
  ]);

  const handleFilterBy = useCallback((value: string) => {
    setFilterBy(value);
  }, []);

  const handleFilter = useCallback(
    (value: string) => {
      setCurrentFilters(value ? { [filterBy]: value } : undefined);
    },
    [filterBy],
  );

  const debounceHandleFilter = debounce(handleFilter, 1000);

  const handleSortList = useCallback(
    (sort: 'title' | 'isActive' | 'expirationDate' | 'createdAt') => {
      setSortBy(sort);
    },
    [],
  );

  const handleOrderList = useCallback((order: 'ASC' | 'DESC') => {
    setCurrentOrdering(order);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  return (
    <DefaultLayout>
      <Box flex="1" borderRadius={8} bg="white" p="8">
        <Flex justify="space-between" align="center">
          <Heading size="lg" fontWeight="normal">
            Anúncios
          </Heading>

          <LinkButton
            colorScheme="green"
            icon={<Icon as={RiAddLine} fontSize="16" />}
            to="/advertisements/register"
          >
            Cadastrar novo
          </LinkButton>
        </Flex>

        <AdvertisementTable
          mt="4"
          advertisements={advertisementsList}
          currentPage={currentPage}
          filterBy={filterBy}
          onChangeFilterBy={handleFilterBy}
          onFilter={debounceHandleFilter}
          onOrder={handleOrderList}
          onPageChange={handlePageChange}
          onSort={handleSortList}
          order={currentOrdering}
          sortBy={sortBy}
          totalPages={totalPages}
        />
      </Box>
    </DefaultLayout>
  );
};
